import {
	ContextService,
	FootballService,
	GameService,
	SportResolverService
} from "@prestosports/prestostats-core/Services";
import {
	FootballState,
	FootballTransition
} from "@prestosports/prestostats-core/Football";
import {
	ADD_PLAYS,
	ADD_PLAYS_COMPLETE,
	APPLY_FILTER,
	CREATE_START_PLAYS_COMPLETE,
	DELETE_PLAY,
	DELETE_PLAY_COMPLETE,
	END_PLAY,
	FINALIZE_PLAY_COMPLETE,
	GAME_CONTROL,
	GET_PLAY,
	GET_PLAY_COMPLETE,
	GO_BACK,
	GO_BACK_COMPLETE,
	GO_FORWARD,
	GO_FORWARD_COMPLETE,
	INIT_PLAYS_DATA,
	INIT_PLAYS_DATA_COMPLETE,
	INSERT_PLAY_COMPLETE,
	REFRESH_PARTICIPANTS,
	REFRESH_PARTICIPANTS_COMPLETE,
	REFRESH_PLAYS_COMPLETE,
	REVERT_TO_TRANSITION,
	REVERT_TO_TRANSITION_COMPLETE,
	SAVE_EDIT_PLAY,
	SAVE_EDIT_PLAY_COMPLETE,
	SAVE_FRAGMENT,
	SAVE_FRAGMENT_COMPLETE,
	SAVE_PLAY,
	SAVE_PLAY_COMPLETE,
	SET_ACTIVE_STEP,
	SET_DRIVE_REPLAY,
	SET_DRIVE_REPLAY_STATS,
	START_FOOTBALL_GAME_COMPLETE,
	START_OVER,
	START_PLAY,
	START_TRANSITION,
	START_TRANSITION_COMPLETE,
	TOGGLE_EDIT_PLAY_MODE,
	UPDATE_PLAY_STATE,
	UPDATE_STEPS
} from "./constants";
// eslint-disable-next-line import/no-cycle
import {
	updateGameContext,
	validateGameData,
	pauseGameClock,
	resumeGameClock
} from "../game-context/actions";
import {
	getGameAccess,
	setConfirmSave as setConfirmSaveAction,
	setEditPlay as setEditPlayAction,
	setInsertPlay as setInsertPlayAction,
	setInsertType as setInsertTypeAction,
	withQueuedSync,
	withSubmitting
} from "../app/actions";
import Alert from "../../util/alert";
import {
	getFormattedTime,
	getPlayNumber
} from "../../views/football/components/utils/PlayUtils";
import { getStreams } from "../stream/actions";
import { refreshTeamsContext } from "../team-context/actions";

export const getAllowedTransitions = async (play, fragment) => {
	const game = await GameService.getGame(play.gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	return service.getAllowedTransitions(play.id, fragment);
};

const mapPlay = (play) => {
	const jsonPlay = play.toJSON();
	jsonPlay.fragments = play.fragments.map((f) => f.toJSON([], []));
	jsonPlay.title = play.title;
	jsonPlay.messages = play.messages;
	jsonPlay.details = play.details;
	jsonPlay.isUpdate = play.isUpdate();
	jsonPlay.isEditable = play.isEditable();

	return jsonPlay;
};

const toPlayState = async (play) => {
	if (play) {
		return {
			allowedTransitions: await getAllowedTransitions(play),
			canGoBack: play.canGoBack(),
			canGoForward: play.canGoForward(),
			currentPlay: mapPlay(play),
			currentState: play.getState(),
			playHistory: play.getHistory(),
			transitionHistory: play.getTransitionHistory(),
			canChangePeriods: play.isInitialStep() && !play.getPendingFragment()
		};
	}

	return {
		allowedTransitions: [],
		canGoBack: false,
		canGoForward: false,
		currentPlay: null,
		currentState: null,
		playHistory: [],
		transitionHistory: [],
		canChangePeriods: true
	};
};

const filterOnlyCompleteWithoutGameStart = (plays) => {
	return plays
		.filter(
			(p) =>
				p.isComplete() &&
				!p.fragments.some((f) => f.associatedState === FootballState.GameStart)
		)
		.sort((a, b) => b.index - a.index);
};

export const addPlays = (plays, dispatch) => {
	dispatch({ type: ADD_PLAYS });

	dispatch({
		type: ADD_PLAYS_COMPLETE,
		payload: { plays: plays.map(mapPlay) }
	});
};

const addPlay = (play, dispatch) => {
	addPlays([play], dispatch);
};

export const startPlay = async (gameId, dispatch) => {
	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const { play, wizardData } = await service.getOrCreatePlay(gameId);

	dispatch({
		type: START_PLAY,
		payload: {
			...(await toPlayState(play)),
			...wizardData
		}
	});
};

export const refreshParticipants = async (gameId, dispatch) => {
	dispatch({ type: REFRESH_PARTICIPANTS });
	try {
		await FootballService.refreshParticipants(gameId);
		dispatch({
			type: REFRESH_PARTICIPANTS_COMPLETE
		});
		await refreshTeamsContext(gameId, dispatch);
	} catch (e) {
		throw Error(e);
	}
};

export const savePlay = async (play, gameContext, dispatch) => {
	dispatch({ type: SAVE_PLAY });
	try {
		const saved = await FootballService.savePlay(play);

		dispatch({
			type: SAVE_PLAY_COMPLETE,
			payload: {
				...(await toPlayState(saved))
			}
		});
	} catch (e) {
		throw Error(e);
	}
};

export const getPlay = async (playId, dispatch) => {
	dispatch({ type: GET_PLAY });

	try {
		const play = await FootballService.getPlay(playId);
		dispatch({
			type: GET_PLAY_COMPLETE,
			payload: {
				...(await toPlayState(play))
			}
		});
	} catch (e) {
		throw Error(e);
	}
};

export const goBack = async (play, dispatch) => {
	dispatch({ type: GO_BACK });
	const game = await GameService.getGame(play.gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const playUpdate = await service.goBack(play.id);
	dispatch({
		type: GO_BACK_COMPLETE,
		payload: {
			...(await toPlayState(playUpdate))
		}
	});
};

export const goForward = async (play, dispatch) => {
	dispatch({ type: GO_FORWARD });
	const game = await GameService.getGame(play.gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const playUpdate = await service.goForward(play.id);

	dispatch({
		type: GO_FORWARD_COMPLETE,
		payload: {
			...(await toPlayState(playUpdate))
		}
	});
};

export const endPlay = (dispatch) => {
	dispatch({
		type: END_PLAY,
		payload: {
			play: null
		}
	});
};

export const setActiveStep = async (
	activeStepIndex,
	activeStep,
	play,
	dispatch
) => {
	const allowedTransitions = await getAllowedTransitions(
		play,
		activeStep?.fragment
	);
	dispatch({
		type: SET_ACTIVE_STEP,
		payload: { activeStep: activeStepIndex, allowedTransitions }
	});
};

export const startOver = async (oldPlay, dispatch) => {
	const game = await GameService.getGame(oldPlay.gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	await service.deletePlay(oldPlay.id);
	const { play, wizardData, gameContext, createdPlays } =
		await service.getOrCreatePlay(oldPlay.gameId);
	const playState = await toPlayState(play);

	dispatch({
		type: START_OVER,
		payload: {
			...playState,
			...wizardData,
			createdPlays: createdPlays.filter((p) => p.isComplete()).map(mapPlay),
			activeStep: wizardData.activeStep
		}
	});

	if (gameContext) {
		updateGameContext(gameContext, dispatch);
	}
};

export const updatePlayState = async (play, dispatch) => {
	const playState = await toPlayState(play);
	dispatch({
		type: UPDATE_PLAY_STATE,
		payload: {
			...playState
		}
	});
};

/**
 * Restore steps for a play at the provided stepIndex
 * @param {FootballPlay} play
 * @param {number} stepIndex
 * @param {(e: *) => void} dispatch
 * @returns {Promise<void>}
 */
export const updateSteps = async (play, stepIndex, dispatch) => {
	const game = await GameService.getGame(play.gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const { steps } = await service.getWizardData(play);
	const activeStep = steps.at(stepIndex);

	dispatch({
		type: UPDATE_STEPS,
		payload: {
			steps,
			activeStep: activeStep.index
		}
	});
};

export const initPlaysData = async (game, dispatch) => {
	dispatch({
		type: INIT_PLAYS_DATA
	});

	const service = await SportResolverService.resolveSportService(game.sportCode);

	const [plays, metadata] = await Promise.all([
		service.getGamePlays(game.id),
		service.getMetadata()
	]);

	const filteredPlays = filterOnlyCompleteWithoutGameStart(plays).map(mapPlay);

	dispatch({
		type: INIT_PLAYS_DATA_COMPLETE,
		payload: {
			plays: filteredPlays,
			filteredPlays,
			metadata
		}
	});
};

const containsText = (play, gameConfig, searchText) => {
	const regex = new RegExp(searchText, "gi");
	const { messages, index, time, period, details, title } = play;
	let matches = false;

	if (title) {
		matches = !!title.match(regex);
	}

	if (messages) {
		matches = matches || !!messages.find((m) => m && m.match(regex));
	}

	if (details) {
		matches = matches || !!details.find((d) => d && d.match(regex));
	}

	if (!matches) {
		matches = [
			getPlayNumber(index),
			getFormattedTime(time, gameConfig, period)
		].some((s) => s === searchText);
	}

	return matches;
};

export const applyFilter = (plays, searchText, gameConfig, dispatch) => {
	dispatch({
		type: APPLY_FILTER,
		payload: {
			filteredPlays: plays.filter((p) => containsText(p, gameConfig, searchText)),
			searchText
		}
	});
};

/**
 * Drive replay is presentation-only state: it names the drive being walked on
 * the field graphic, which of its plays is showing, and whether the walk is
 * running. Pass null to stop replaying.
 */
export const setDriveReplay = (driveReplay, dispatch) => {
	dispatch({
		type: SET_DRIVE_REPLAY,
		payload: { driveReplay }
	});
};

/**
 * The stat records the rules engine wrote for this drive, so replay can show
 * what each player did on it. Read from the stats service rather than summed
 * off the field path: the engine is the authority on who gets credited what
 * (a completion is yards for the passer AND the receiver, a sack is yards
 * against, and so on).
 */
export const loadDriveReplayStats = async (gameId, driveId, dispatch) => {
	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveStatsService(game.sportCode);

	dispatch({
		type: SET_DRIVE_REPLAY_STATS,
		payload: { driveReplayStats: await service.getByDriveId(gameId, driveId) }
	});
};

export const startTransition = async (
	currentPlay,
	transitionName,
	fromHistoryIndex,
	dispatch
) =>
	withSubmitting(async () => {
		// Pause the game clock BEFORE the core call so any time read by
		// core (play.time, fragment defaults, drive snapshots) sees the
		// paused value, not a stale moving target. Marked as
		// byPlaySequence so finalize/startOver knows to auto-resume.
		await pauseGameClock(currentPlay.gameId, dispatch, {
			byPlaySequence: true
		});
		dispatch({ type: START_TRANSITION });
		const game = await GameService.getGame(currentPlay.gameId);
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);
		const { play, wizardData, gameContext, createdPlays } =
			await service.startTransition(
				currentPlay.id,
				transitionName,
				fromHistoryIndex
			);

		if (play.isComplete()) {
			addPlay(play, dispatch);
			startPlay(play.gameId, dispatch);
		} else {
			dispatch({
				type: START_TRANSITION_COMPLETE,
				payload: {
					...(await toPlayState(play)),
					...wizardData,
					createdPlays: createdPlays.filter((p) => p.isComplete()).map(mapPlay)
				}
			});
		}

		if (gameContext) {
			const { gameClock: _gc, ...contextWithoutClock } = gameContext;
			updateGameContext(contextWithoutClock, dispatch);
		}
	}, dispatch);

export const revertToTransition = async (
	currentPlay,
	associatedHistoryIndex,
	dispatch
) =>
	withSubmitting(async () => {
		dispatch({ type: REVERT_TO_TRANSITION });
		const game = await GameService.getGame(currentPlay.gameId);
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);

		const { play, wizardData } = await service.revertToTransition(
			currentPlay.id,
			associatedHistoryIndex
		);

		dispatch({
			type: REVERT_TO_TRANSITION_COMPLETE,
			payload: {
				...(await toPlayState(play)),
				...wizardData,
				createdPlays: []
			}
		});
	}, dispatch);

export const saveFragment = async (fragment, goToStep = null, dispatch) =>
	withSubmitting(async () => {
		dispatch({ type: SAVE_FRAGMENT });
		const { play, wizardData } = await FootballService.saveFragment(fragment);
		dispatch({
			type: SAVE_FRAGMENT_COMPLETE,
			payload: {
				...(await toPlayState(play)),
				steps: wizardData.steps,
				activeStep: goToStep || wizardData.activeStep
			}
		});
	}, dispatch);

export const resetInsert = async (gameId, dispatch) => {
	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);

	await service.cancelPlayInsertion(gameId);
	setInsertPlayAction(null, dispatch);
	const { play } = await service.getOrCreatePlay(gameId);
	await startOver(play, dispatch);
};

export const toggleInsertPlayMode = async (
	playId,
	insertType,
	gameId,
	dispatch
) => {
	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	if (!playId) {
		await resetInsert(game.id, dispatch);
	} else {
		const { play: pendingPlay } = await service.getOrCreatePlay(gameId);
		await startOver(pendingPlay, dispatch);
		const insertPlay = await service.getPlay(playId);
		const { gameContext, play, activeStep } = await service.initPlayInsertion(
			gameId,
			insertPlay,
			insertType
		);
		await setInsertPlayAction(insertPlay, dispatch);
		await setInsertTypeAction(insertType, dispatch);
		await updateGameContext(gameContext, dispatch);
		await updatePlayState(play, dispatch);
		if (activeStep) {
			await updateSteps(play, activeStep, dispatch);
		}
	}
};

export const toggleEditPlayMode = async (playId, gameId, dispatch) => {
	if (playId) {
		const game = await GameService.getGame(gameId);
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);
		const play = await service.getPlay(playId);
		const { steps } = await service.getWizardData(play);
		const firstByStep = steps.findIndex((step) => step.isEditable());
		if (firstByStep >= 0) {
			await setEditPlayAction({ ...mapPlay(play) }, dispatch);

			dispatch({
				type: TOGGLE_EDIT_PLAY_MODE,
				payload: {
					...(await toPlayState(play)),
					steps,
					activeStep: firstByStep
				}
			});
		}
	} else {
		await startPlay(gameId, dispatch);
		await setEditPlayAction(null, dispatch);
	}
};

export const saveEditPlay = async (editPlay, currentEventId, dispatch) =>
	withSubmitting(async () => {
		await toggleEditPlayMode(null, editPlay.gameId, dispatch);
		await setConfirmSaveAction(false, null, dispatch);

		const game = await GameService.getGame(editPlay.gameId);
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);

		return withQueuedSync(
			currentEventId,
			async () => {
				dispatch({ type: SAVE_EDIT_PLAY });
				const { play, gameContext } = await service.editPlay(editPlay);

				updateGameContext(gameContext, dispatch);

				dispatch({
					type: SAVE_EDIT_PLAY_COMPLETE,
					payload: {
						play: mapPlay(play)
					}
				});
			},
			dispatch
		);
	}, dispatch);

export const setStartSettings = async (gameId, startSettings, dispatch) => {
	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const { play, gameContext, wizardData, createdPlays } =
		await service.setStartSettings(gameId, startSettings);

	updateGameContext(gameContext, dispatch);

	if (createdPlays) {
		dispatch({
			type: CREATE_START_PLAYS_COMPLETE,
			payload: { ...(await toPlayState(play)), ...wizardData, createdPlays }
		});
	}
};

export const startGame = async (gameId, startSettings, dispatch) => {
	withSubmitting(async () => {
		const game = await GameService.getGame(gameId);
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);
		const { play, wizardData, gameContext, createdPlays } =
			await service.startGame(gameId, startSettings);

		if (gameContext) {
			updateGameContext(gameContext, dispatch);
			validateGameData(gameId, dispatch);
			getGameAccess(gameId, dispatch);
			await refreshParticipants(gameId, dispatch);
		}

		dispatch({
			type: START_FOOTBALL_GAME_COMPLETE,
			payload: {
				...(await toPlayState(play)),
				...wizardData,
				createdPlays: filterOnlyCompleteWithoutGameStart(createdPlays).map(mapPlay)
			}
		});
	}, dispatch);
};

export const refreshPlays = (plays, dispatch) => {
	dispatch({
		type: REFRESH_PLAYS_COMPLETE,
		payload: {
			plays: filterOnlyCompleteWithoutGameStart(plays).map((p) => mapPlay(p))
		}
	});
};

export const finalizePlay = async (
	playId,
	sportCode,
	gameId,
	currentEventId,
	dispatch
) =>
	withSubmitting(async () => {
		return withQueuedSync(
			currentEventId,
			async () => {
				try {
					const service = await SportResolverService.resolveSportService(sportCode);
					const { gameContext, createdPlays } = await service.finalizePlay(playId);

					if (gameContext) {
						const { gameClock: _gc, ...contextWithoutClock } = gameContext;
						updateGameContext(contextWithoutClock, dispatch);
					}

					dispatch({
						type: FINALIZE_PLAY_COMPLETE,
						payload: {
							...(await toPlayState(null)),
							steps: [],
							activeStep: null,
							createdPlays:
								filterOnlyCompleteWithoutGameStart(createdPlays).map(mapPlay)
						}
					});

					refreshPlays(await service.getGamePlays(gameId), dispatch);
					await refreshParticipants(gameContext.gameId, dispatch);

					// Resume running clock if it was paused by the play sequence.
					// onlyIfByPlaySequence preserves any manual pauses.
					await resumeGameClock(gameId, dispatch, {
						onlyIfByPlaySequence: true
					});

					await startPlay(gameContext.gameId, dispatch);
				} catch (ex) {
					Alert.error(ex.message);
					console.error(ex);
				}
			},
			dispatch
		);
	}, dispatch);

export const insertNewPlay = async (
	playId,
	gameId,
	sportCode,
	currentEventId,
	insertPlayIndex,
	insertType,
	dispatch
) =>
	withSubmitting(async () => {
		return withQueuedSync(
			currentEventId,
			async () => {
				try {
					const service = await SportResolverService.resolveSportService(sportCode);
					const gameContext = await service.insertNewPlay(
						playId,
						insertPlayIndex,
						insertType
					);

					updateGameContext(gameContext, dispatch);
					setInsertPlayAction(null, dispatch);

					const updatedPlays = (await service.getGamePlays(gameId)).filter((p) =>
						p.isComplete()
					);

					refreshPlays(updatedPlays, dispatch);
					await refreshParticipants(gameId, dispatch);
					getStreams(currentEventId, dispatch);
					await startPlay(gameId, dispatch);

					dispatch({
						type: INSERT_PLAY_COMPLETE
					});
				} catch (ex) {
					Alert.error(ex.message);
					console.error(ex);
				}
			},
			dispatch
		);
	}, dispatch);

export const insertTimePlay = async (
	gameId,
	seconds,
	period,
	sportCode,
	currentEventId,
	insertPlayIndex,
	insertType,
	dispatch
) => {
	const service = await SportResolverService.resolveSportService(sportCode);
	const { play } = await service.getOrCreatePlay(gameId);
	await startOver(play, dispatch);
	const { id: playId } = await service.savePlay(
		await service.initTimeAndPeriodPlay(gameId, seconds, period)
	);

	insertNewPlay(
		playId,
		gameId,
		sportCode,
		currentEventId,
		insertPlayIndex,
		insertType,
		dispatch
	);
};

export const deletePlay = async (
	playId,
	currentPlay,
	currentEventId,
	dispatch
) =>
	withSubmitting(async () => {
		return withQueuedSync(
			currentEventId,
			async () => {
				dispatch({ type: DELETE_PLAY });
				const game = await GameService.getGame(currentPlay.gameId);
				const service = await SportResolverService.resolveSportService(
					game.sportCode
				);

				const gameContext = await service.deletePlayAndUpdateContext(playId);
				const updatedPlays = (await service.getGamePlays(game.id)).filter((p) =>
					p.isComplete()
				);

				refreshPlays(updatedPlays, dispatch);
				await refreshParticipants(game.id, dispatch);
				getStreams(currentEventId, dispatch);

				// After deletion, gameClock has been restored by core via
				// service.deletePlayAndUpdateContext (returned gameContext).
				// The clock state in IndexedDB and Redux already reflects
				// the post-delete value.

				dispatch({
					type: DELETE_PLAY_COMPLETE,
					payload: {
						playId
					}
				});

				if (gameContext) {
					updateGameContext(gameContext, dispatch);
				}

				if (gameContext?.coinTossRequired) {
					// Core has left the pending play on the coin toss step, so
					// read it back instead of starting over — startOver deletes
					// that play and would recreate it without the toss.
					await startPlay(game.id, dispatch);
				} else {
					await startOver(currentPlay, dispatch);
				}
			},
			dispatch
		);
	}, dispatch);

export const gameControl = async (gameId, dispatch) => {
	dispatch({
		type: GAME_CONTROL
	});

	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);

	// Delete any previous pending play and recreate a new game control play.
	const { play: currentPlay } = await service.getOrCreatePlay(gameId);
	if (currentPlay.id) {
		await service.deletePlay(currentPlay.id);
	}
	const { play } = await service.getOrCreatePlay(gameId);

	await startTransition(
		play,
		FootballTransition.GameControl,
		undefined,
		dispatch
	);

	setEditPlayAction(null, dispatch);
};

export const triggerNewPeriodDialog = async (gameId, trigger, dispatch) => {
	const gameContext = await ContextService.getGameContext(gameId);
	gameContext.triggerNewPeriodNotification = trigger;
	const savedGameContext = await ContextService.updateContext(gameContext);
	updateGameContext(savedGameContext, dispatch);
};
